import dayjs from "dayjs";
import i18n from "../../../../i18n/i18n";

/**
 * Rows of the vehicle sheet's Kilométrage panel.
 *
 * WHAT WAS WRONG
 * --------------
 * This file used to build two rows and put `contrat.contract_km` in BOTH
 * subtitles. That value is the CONTRACTUAL allowance — the mileage the lease
 * permits — not a reading. So the panel could never show a kilometre the
 * vehicle had actually done, and a relevé entered by the driver in the mobile
 * app never appeared on Fleet at all. The second row even called itself
 * "Relevés du <date de début de contrat>" while displaying the allowance.
 *
 * The three rows now answer three different questions:
 *   - Forfait contractuel : what the lease allows (contract_km)
 *   - Dernier relevé      : what the driver last declared, and when
 *   - Écart               : allowance minus reading, i.e. what is left before
 *                           the over-mileage penalty. This is the number a
 *                           fleet manager opens the panel for.
 *
 * The reading comes from the vehicle sheet (`currentMileage`), which the API
 * derives from the events — the same source of truth as
 * GET /vehicle/{id}/mileage and as the server-side coherence rule.
 */

const NBSP = "\u00A0";

/** 125000 -> "125 000" (narrow-space grouping, locale-independent). */
const formatKm = (value: number): string =>
  String(Math.round(value)).replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);

export type TMileageRow = {
  icoName: string;
  title: string;
  subTitle: string;
  /** Set when the row carries a warning (allowance exceeded). */
  isAlert?: boolean;
};

export const transformDataMileages = (
  contrat: Tcontrat | undefined,
  vehicule?: TVehiculeOne
): TMileageRow[] => {
  const contractKm =
    typeof contrat?.contract_km === "number" ? contrat.contract_km : null;

  // `currentMileage` is the max over readings; `lastMileage` is the most recent
  // one. They differ only on histories written before the coherence rule
  // existed. The panel shows the max — it is what the odometer is known to have
  // passed — and dates it with the last declaration.
  const currentMileage =
    typeof vehicule?.currentMileage === "number" ? vehicule.currentMileage : null;
  const lastMileageDate = vehicule?.lastMileageDate ?? null;

  const rows: TMileageRow[] = [
    {
      icoName: "fileStack",
      title: i18n.t("panel.mileage.contractAllowance", "Contract allowance"),
      subTitle:
        contractKm !== null
          ? `${formatKm(contractKm)} km`
          : i18n.t("panel.mileage.noContract", "No contract"),
    },
    {
      icoName: "license",
      title:
        lastMileageDate
          ? `${i18n.t("panel.mileage.lastStatement", "Last statement")} · ${dayjs(
              lastMileageDate
            ).format("DD/MM/YYYY")}`
          : i18n.t("panel.mileage.lastStatement", "Last statement"),
      subTitle:
        currentMileage !== null
          ? `${formatKm(currentMileage)} km`
          : // Explicit rather than "0 km": a vehicle nobody has read is not a
            // vehicle at zero kilometres, and showing 0 made the panel look
            // populated when it was empty. It is also the state the "Relancer
            // le collaborateur" button exists to resolve.
            i18n.t("panel.mileage.noStatement", "No statement yet"),
    },
  ];

  // The gap only means something when both numbers exist.
  if (contractKm !== null && currentMileage !== null) {
    const remaining = contractKm - currentMileage;
    const exceeded = remaining < 0;

    rows.push({
      icoName: exceeded ? "alertTriangle" : "analytics",
      title: exceeded
        ? i18n.t("panel.mileage.overAllowance", "Over allowance")
        : i18n.t("panel.mileage.remaining", "Remaining before penalty"),
      subTitle: `${formatKm(Math.abs(remaining))} km`,
      isAlert: exceeded,
    });
  }

  return rows;
};
